import { format } from "date-fns";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCitiesAndCounties,
  fetchExpertRecruitmentAreas,
} from "../app/appSlice";
import { FeatureFlagName } from "../app/featureFlags/featureFlags";
import { canViewFeature } from "../app/featureFlags/helpers";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setMessagingMaintenanceRetryHandler } from "../app/messagingMaintenanceRetry";
import { isoFormat } from "../common/helpers/formatDateTime";
import {
  fetchHrTasklist,
  talentsDashboardCounts,
} from "../features/dashboard/slice/dashboardActions";
import { fetchUnacknowledgedFeedbacksCount } from "../features/feedbacks/FeedbacksSlice";
import { fetchInboxSummary } from "../features/messaging/messagingActions";
import { fetchActiveAnnouncements } from "../features/settings/announcements/announcementSlice";
import { fetchRecruitmentAreas } from "../features/settings/recruitment/recruitmentsSlice";
import { fetchSkills } from "../features/skills/skillsSlice";
import { fetchCertificateDefinitionsAutocomplete } from "../features/talents/talentsSlice";
import { canExpertUseMessaging } from "../features/user/accessRightChecks";
import { fetchExpertUsers } from "../features/user/expertUsersSlice";
import { fetchWorkTitles } from "../features/workTitles/workTitleSlice";
import useLanguageSkillsQuery from "./useLanguageSkillsQuery";
import { useLocalizationSupportIndex } from "./useLocalizationSupportIndex";

const COUNT_INTERVAL = 15 * 60 * 1000; // 15 minutes

const useFetchInitialData = () => {
  const { user } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const language = i18n.language.substring(0, 2);

  const { isCompleted, error } = useLocalizationSupportIndex(!!user);
  useLanguageSkillsQuery(!!user);

  useEffect(() => {
    if (!user) return;
    dispatch(fetchCitiesAndCounties());
    dispatch(fetchExpertUsers({}));

    // TODO: consider using a single fetch for all dashboard data
    const params = {
      atDate: format(new Date(), isoFormat),
      pageSize: 50,
    };
    dispatch(fetchHrTasklist(params));
    dispatch(talentsDashboardCounts({ hrRepresentative: user.id }));
    dispatch(fetchRecruitmentAreas({}));
    dispatch(fetchActiveAnnouncements());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id]);

  useEffect(() => {
    dispatch(fetchSkills({ language }));
    dispatch(fetchCertificateDefinitionsAutocomplete({ language }));
    dispatch(fetchWorkTitles({ language }));
  }, [dispatch, language]);

  useEffect(() => {
    if (!user) return;
    dispatch(fetchUnacknowledgedFeedbacksCount({}));
    dispatch(fetchExpertRecruitmentAreas({ expertId: user.id }));
    if (
      canViewFeature(FeatureFlagName.MESSAGING, user) &&
      canExpertUseMessaging(user)
    ) {
      dispatch(fetchInboxSummary());
    }

    const id = setInterval(() => {
      dispatch(fetchUnacknowledgedFeedbacksCount({}));
    }, COUNT_INTERVAL);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (
      !user ||
      !canViewFeature(FeatureFlagName.MESSAGING, user) ||
      !canExpertUseMessaging(user)
    ) {
      return;
    }

    // Stores the handler. messageApi calls it after a 503 Retry-After.
    setMessagingMaintenanceRetryHandler(() => {
      dispatch(fetchInboxSummary());
    });

    return () => setMessagingMaintenanceRetryHandler(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id]);

  return { isLocalizationCompleted: isCompleted, localizationError: error };
};

export default useFetchInitialData;
